import React, {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAuthClient} from '../../../../../utils/use-auth-client'
import dayjs from 'dayjs'

import {
  DrawerWrapper,
  DrawerHeader,
  DrawerHeaderLeft,
  DrawerIconBox,
  DrawerTitleGroup,
  DrawerLabel,
  DrawerTitle,
  CloseButton,
  DrawerBody,
  Section,
  SectionTitle,
  DetailsGrid,
  GridCell,
  DetailCellWrapper,
  DetailCellIcon,
  DetailCellContent,
  DetailCellLabel,
  DetailCellValue,
  PayBadge,
  SubscriptionsPanel,
  SubscriptionTag,
  PanelMessage,
  DotsLoader,
  Dot,
} from './SubscriptionDrawer.styles'
import {fetchSubscriptions} from '../../../../../app-store/accounts/accountsThunk'
import {
  StyledBuildingIcon,
  StyledCalendarDayIcon,
  StyledChooseIcon,
  StyledCloseIcon,
  StyledFileInvoiceDollarIcon,
  StyledIdCardIcon,
  StyledMailIcon,
} from '../../../../shared/icons/icons.styles'

// Detail Cell

function DetailCell({icon, label, value, children}) {
  return (
    <DetailCellWrapper>
      <DetailCellIcon>{icon}</DetailCellIcon>
      <DetailCellContent>
        <DetailCellLabel>{label}</DetailCellLabel>
        {children ?? <DetailCellValue>{value ?? '—'}</DetailCellValue>}
      </DetailCellContent>
    </DetailCellWrapper>
  )
}

// Drawer

function SubscriptionDrawer({loc, prefetchedData, onClose, source}) {
  const dispatch = useDispatch()
  const client = useAuthClient()

  const {
    results,
    loading: reduxLoading,
    error: reduxError,
  } = useSelector(state => state.accounts.subscriptions)

  // When prefetchedData is provided (All Accounts pre-fetches for counting),
  // use it directly and skip the redundant network call.
  const liveData = prefetchedData ?? results
  const loading = prefetchedData ? false : reduxLoading
  const error = prefetchedData ? null : reduxError
  const {childSubscriptions = [], recurlyAccount} = liveData ?? {}

  // Always prefer live Recurly data (childSubscriptions from fetchSubscriptions).
  // Use loc.subscriptions (RECURLY_SUBSCRIPTIONS cached table) as a placeholder
  // while the live fetch is in-flight — avoids a blank panel on first open.
  const locSubs = loc?.subscriptions ?? []
  const FREE_PLAN_NAMES = ['Website Builder', 'Signatures Usage']
  const isFreePlan = name =>
    FREE_PLAN_NAMES.some(free => name?.startsWith(free))
  const allTags =
    childSubscriptions.length > 0
      ? childSubscriptions
          .flatMap(sub => [
            {id: sub.id, name: sub.name},
            ...(sub.addons ?? []).map(addon => ({
              id: addon.id,
              name: addon.name,
            })),
          ])
          .filter(tag => !isFreePlan(tag.name))
      : locSubs
          .map(sub => ({
            id: sub.subscriptionUuid || sub.planCode,
            name: sub.planName || sub.planCode,
            state: sub.state,
          }))
          .filter(tag => !isFreePlan(tag.name))

  const billTo = recurlyAccount?.billTo ?? loc?.billTo
  // Live data uses startDate; cached subs use activatedAt / subscriptionCreatedAt
  const rawDate =
    childSubscriptions[0]?.startDate ||
    locSubs[0]?.activatedAt ||
    locSubs[0]?.subscriptionCreatedAt
  const startDate = rawDate ? dayjs(rawDate).format('MMMM D, YYYY') : '—'

  useEffect(() => {
    // Skip the fetch when the caller already pre-fetched subscription data
    // (e.g. All Accounts fetches it upfront for the subs count badge).
    if (prefetchedData) return
    // Always fetch live subscriptions from Recurly so newly created subscriptions
    // appear immediately without waiting for the RECURLY_SUBSCRIPTIONS sync.
    // Billing service children from the supplement path store the EAID in `id`
    // with `code` absent — fall back to `id` so the fetch always fires.
    const fetchId =
      source === 'my-accounts' ? loc?.thryvId || loc?.code : loc?.id
    if (fetchId) {
      dispatch(fetchSubscriptions({client, thryvId: fetchId}))
    }
  }, [loc?.thryvId, loc?.code, loc?.id])

  return (
    <DrawerWrapper>
      <DrawerHeader>
        <DrawerHeaderLeft>
          <DrawerIconBox>
            <StyledBuildingIcon />
          </DrawerIconBox>
          <DrawerTitleGroup>
            <DrawerLabel>Subscription Details</DrawerLabel>
            <DrawerTitle>{loc?.name}</DrawerTitle>
          </DrawerTitleGroup>
        </DrawerHeaderLeft>

        <CloseButton onClick={onClose} aria-label="Close">
          <StyledCloseIcon />
        </CloseButton>
      </DrawerHeader>

      <DrawerBody>
        {/* Details */}
        <Section>
          <SectionTitle>Billing Details</SectionTitle>
          <DetailsGrid>
            {/* Row 1 */}
            <GridCell borderRight borderBottom>
              <DetailCell
                icon={<StyledIdCardIcon />}
                label="Thryv ID"
                value={loc?.code || loc?.thryvId || loc?.id}
              />
            </GridCell>
            <GridCell borderBottom>
              <DetailCell
                icon={<StyledChooseIcon />}
                label="EID"
                value={null}
              />
            </GridCell>

            {/* Row 2 */}
            <GridCell borderRight borderBottom>
              <DetailCell
                icon={<StyledFileInvoiceDollarIcon />}
                label="Billed To"
              >
                <PayBadge>{billTo === 'self' ? 'Self' : 'Parent'}</PayBadge>
              </DetailCell>
            </GridCell>
            <GridCell borderBottom>
              <DetailCell
                icon={<StyledCalendarDayIcon />}
                label="Start Date"
                value={startDate}
              />
            </GridCell>

            {/* Row 3 — full width */}
            <GridCell fullWidth>
              <DetailCell
                icon={<StyledMailIcon />}
                label="Email"
                value={loc?.email}
              />
            </GridCell>
          </DetailsGrid>
        </Section>

        {/* Subscriptions */}
        <Section>
          <SectionTitle>Subscription Details</SectionTitle>
          <SubscriptionsPanel>
            {loading && allTags.length === 0 && (
              <DotsLoader centered>
                {[0, 1, 2].map(i => (
                  <Dot key={i} index={i} />
                ))}
              </DotsLoader>
            )}
            {error && !loading && allTags.length === 0 && (
              <PanelMessage isError>Failed to load subscriptions.</PanelMessage>
            )}
            {!loading && !error && allTags.length === 0 && (
              <PanelMessage>No subscriptions found.</PanelMessage>
            )}
            {allTags.map(tag => (
              <SubscriptionTag key={tag.id}>{tag.name}</SubscriptionTag>
            ))}
            {loading &&
              childSubscriptions.length === 0 &&
              allTags.length > 0 && (
                <DotsLoader>
                  {[0, 1, 2].map(i => (
                    <Dot key={i} index={i} />
                  ))}
                </DotsLoader>
              )}
          </SubscriptionsPanel>
        </Section>
      </DrawerBody>
    </DrawerWrapper>
  )
}

export default SubscriptionDrawer
